/* Befehle des Mapeditors: reine Funktionen auf (Dokument, Auswahl). Keine DOM-Abhängigkeit.
 * Auswahl sel = { b: Set<Brush-ID>, e: Set<Entity-ID> } (e: nur Punkt-Entities). */
import type { BBox, Brush, Doc, Entity, Face, Sel, Vec3 } from './types.ts';
import type { FacePatch } from './texalign.ts';
import type { FaceRef } from './find.ts';
import Core from '../core.ts';
import EntDefs from './entdefs.ts';
import { volume } from './simplify.ts';
/** Zwischenablage: Brushes mit ihrer Entity (null = Welt) und Punkt-Entities */
export interface Clip { groups: { ent: Entity | null; brushes: Brush[] }[]; ents: Entity[] }
/** Ausgewählte Eckpunkte eines Brushes */
export interface VertGroup { id: string; idxs: number[] }
/** Punktauswahl in einer 2D-Ansicht: Achsen h/v und die Lage pt in dieser Projektion */
export interface VertSel { h: number; v: number; pt: [number, number] }
type FacePatchFn = FacePatch | ((b: Brush, fi: number) => FacePatch | null);

const Cmd = (function (Core, EntDefs) {
  'use strict';

  const emptySel = (): Sel => ({ b: new Set<string>(), e: new Set<string>() });
  const cloneSel = (s: Sel): Sel => ({ b: new Set(s.b), e: new Set(s.e) });
  const isEmpty = (s: Sel): boolean => !s.b.size && !s.e.size;
  const isPointEnt = (e: Entity, i: number): boolean => i > 0 && !e.brushes.length && e.kv.origin !== undefined;
  const fmtOrigin = (o: number[]): string => o.map((x) => String(Math.round(x * 1000) / 1000)).join(' ');

  function selBrushes(doc: Doc, sel: Sel): Brush[] {
    const r: Brush[] = [];
    doc.ents.forEach((e) => e.brushes.forEach((b) => { if (sel.b.has(b.id)) r.push(b); }));
    return r;
  }
  function selEnts(doc: Doc, sel: Sel): Entity[] { return doc.ents.filter((e, i) => sel.e.has(e.id) && isPointEnt(e, i)); }
  function selBBox(doc: Doc, sel: Sel): BBox | null {
    const bs = selBrushes(doc, sel), es = selEnts(doc, sel);
    if (!bs.length && !es.length) return null;
    const bb: BBox = bs.length ? Core.bboxOf(bs) : { min: [Infinity, Infinity, Infinity], max: [-Infinity, -Infinity, -Infinity] };
    es.forEach((e) => {
      const o = Core.parseVec(e.kv.origin);
      if (!o) return;
      for (let i = 0; i < 3; i++) { if (o[i] < bb.min[i]) bb.min[i] = o[i]; if (o[i] > bb.max[i]) bb.max[i] = o[i]; }
    });
    return bb;
  }
  function withOrigin(e: Entity, o: number[]): Entity { return Object.assign({}, e, { kv: Object.assign({}, e.kv, { origin: fmtOrigin(o) }) }); }
  function mapEnts(doc: Doc, sel: Sel, fn: (e: Entity) => Entity): Doc {
    return { ents: doc.ents.map((e, i) => (sel.e.has(e.id) && isPointEnt(e, i) ? fn(e) : e)) };
  }
  /** Leere Brush-Entities entfernen (nie die Welt, nie Entities mit origin). */
  function cleanup(doc: Doc): Doc {
    const ents = doc.ents.filter((e, i) => i === 0 || e.brushes.length || e.kv.origin !== undefined);
    return ents.length === doc.ents.length ? doc : { ents: ents };
  }

  function move(doc: Doc, sel: Sel, d: Vec3, lock?: boolean): Doc {
    let r = Core.mapBrushes(doc, sel.b, (b) => Core.translateBrush(b, d, lock));
    r = mapEnts(r, sel, (e) => { const o = Core.parseVec(e.kv.origin); return o ? withOrigin(e, Core.add(o, d)) : e; });
    return r;
  }
  function resize(doc: Doc, sel: Sel, oldBox: BBox, newBox: BBox): Doc {
    const f = [0, 1, 2].map((i) => { const o = oldBox.max[i] - oldBox.min[i]; return o < 1e-9 ? 1 : (newBox.max[i] - newBox.min[i]) / o; });
    let r = Core.mapBrushes(doc, sel.b, (b) => Core.scaleBrush(b, oldBox, newBox) || b);
    r = mapEnts(r, sel, (e) => {
      const o = Core.parseVec(e.kv.origin);
      if (!o) return e;
      return withOrigin(e, o.map((x, i) => Math.round(newBox.min[i] + (x - oldBox.min[i]) * f[i])));
    });
    return r;
  }
  /** Setzt die Auswahl-Box neu: reine Verschiebung bleibt verlustfrei (Textur-Lock), sonst Skalierung. */
  function setBBox(doc: Doc, sel: Sel, newBox: BBox, lock?: boolean): Doc {
    const old = selBBox(doc, sel);
    if (!old) return doc;
    const same = [0, 1, 2].every((i) => Math.abs((old.max[i] - old.min[i]) - (newBox.max[i] - newBox.min[i])) < 1e-9);
    if (same) return move(doc, sel, [0, 1, 2].map((i) => newBox.min[i] - old.min[i]) as Vec3, lock);
    return resize(doc, sel, old, newBox);
  }
  function remove(doc: Doc, sel: Sel): { doc: Doc; sel: Sel } {
    let r = Core.mapBrushes(doc, sel.b, () => null);
    r = { ents: r.ents.filter((e, i) => !(sel.e.has(e.id) && isPointEnt(e, i))) };
    return { doc: cleanup(r), sel: emptySel() };
  }
  function rotate(doc: Doc, sel: Sel, axis: number, deg: number, center: Vec3, lock?: boolean): Doc {
    let r = Core.mapBrushes(doc, sel.b, (b) => Core.rotateAround(b, axis, deg, center, lock));
    const M = Core.rotMat(axis, deg);
    r = mapEnts(r, sel, (e) => {
      const o = Core.parseVec(e.kv.origin);
      if (!o) return e;
      const q = Core.sub(o, center), p: Vec3 = [Core.dot(M[0], q), Core.dot(M[1], q), Core.dot(M[2], q)];
      return withOrigin(e, Core.add(p, center));
    });
    return r;
  }
  function mirror(doc: Doc, sel: Sel, axis: number, center: Vec3, lock?: boolean): Doc {
    let r = Core.mapBrushes(doc, sel.b, (b) => Core.mirrorAround(b, axis, center, lock));
    r = mapEnts(r, sel, (e) => {
      const o = Core.parseVec(e.kv.origin);
      if (!o) return e;
      const p = o.slice() as Vec3; p[axis] = 2 * center[axis] - p[axis];
      return withOrigin(e, p);
    });
    return r;
  }

  // ---- Zwischenablage / Duplizieren
  function toClip(doc: Doc, sel: Sel): Clip {
    const groups: Clip['groups'] = [];
    doc.ents.forEach((e, i) => {
      const bs = e.brushes.filter((b) => sel.b.has(b.id));
      if (bs.length) groups.push({ ent: i === 0 ? null : e, brushes: bs });
    });
    return { groups: groups, ents: selEnts(doc, sel) };
  }
  function paste(doc: Doc, clip: Clip, offset: Vec3): { doc: Doc; sel: Sel } {
    const sel = emptySel();
    let r = doc;
    clip.groups.forEach((g) => {
      const copies = g.brushes.map((b) => {
        const c = Core.translateBrush(b, offset, true);
        c.id = Core.newId(); sel.b.add(c.id);
        return c;
      });
      if (!g.ent) r = Core.addBrushes(r, copies, 0);
      else r = Core.addEntity(r, { id: Core.newId(), kv: Object.assign({}, g.ent.kv), brushes: copies });
    });
    clip.ents.forEach((e) => {
      const o = Core.parseVec(e.kv.origin);
      const n = Object.assign({}, e, { id: Core.newId(), kv: Object.assign({}, e.kv), brushes: [] });
      if (o) n.kv.origin = fmtOrigin(Core.add(o, offset));
      r = Core.addEntity(r, n); sel.e.add(n.id);
    });
    return { doc: r, sel: sel };
  }
  const duplicate = (doc: Doc, sel: Sel, offset: Vec3) => paste(doc, toClip(doc, sel), offset);

  // ---- Erzeugen
  function addBrushList(doc: Doc, brushes: Brush[]): { doc: Doc; sel: Sel } {
    const sel = emptySel();
    brushes.forEach((b) => sel.b.add(b.id));
    return { doc: Core.addBrushes(doc, brushes, 0), sel: sel };
  }
  function createEntity(doc: Doc, cls: string, origin?: Vec3 | null): { doc: Doc; sel: Sel; id: string } {
    const def = EntDefs.get(cls);
    const e: Entity = { id: Core.newId(), kv: Object.assign({ classname: cls }, def.d, origin ? { origin: fmtOrigin(origin) } : {}), brushes: [] };
    const sel = emptySel(); sel.e.add(e.id);
    return { doc: Core.addEntity(doc, e), sel: sel, id: e.id };
  }

  // ---- Texturen
  function applyTexture(doc: Doc, sel: Sel, tex: string): Doc {
    return Core.mapBrushes(doc, sel.b, (b) => Object.assign({}, b, { srcText: undefined, faces: b.faces.map((f) => Object.assign({}, f, { tex: tex })) }));
  }
  function setFace(doc: Doc, brushId: string, faceIdx: number, patch: FacePatch): Doc {
    return Core.mapBrushes(doc, new Set([brushId]), (b) => Object.assign({}, b, {
      srcText: undefined,
      faces: b.faces.map((f, i) => (i === faceIdx ? Object.assign({}, f, patch) : f))
    }));
  }

  /** Patch (Objekt oder Funktion (Brush, Index) -> Objekt|null) auf viele Flächen: faces = [{b, f}] */
  function setFaces(doc: Doc, faces: FaceRef[], patch: FacePatchFn): Doc {
    const by = new Map<string, Set<number>>(); faces.forEach((x) => { let s = by.get(x.b); if (!s) { s = new Set(); by.set(x.b, s); } s.add(x.f); });
    return Core.mapBrushes(doc, new Set(by.keys()), (b) => {
      const fs = by.get(b.id) as Set<number>;
      return Object.assign({}, b, { srcText: undefined, faces: b.faces.map((f, i) => { if (!fs.has(i)) return f; const p = typeof patch === 'function' ? patch(b, i) : patch; return p ? Object.assign({}, f, p) : f; }) });
    });
  }

  // ---- Entities bearbeiten
  function setKV(doc: Doc, id: string, key: string, value: string): Doc {
    return Core.updateEntity(doc, id, (e) => Object.assign({}, e, { kv: Object.assign({}, e.kv, { [key]: value }) }));
  }
  function delKV(doc: Doc, id: string, key: string): Doc {
    return Core.updateEntity(doc, id, (e) => { const kv = Object.assign({}, e.kv); delete kv[key]; return Object.assign({}, e, { kv: kv }); });
  }
  function renameKV(doc: Doc, id: string, oldKey: string, newKey: string): Doc {
    return Core.updateEntity(doc, id, (e) => {
      const kv: Record<string, string> = {};
      Object.keys(e.kv).forEach((k) => { kv[k === oldKey ? newKey : k] = e.kv[k]; });
      return Object.assign({}, e, { kv: kv });
    });
  }
  /** Ausgewählte Brushes zu einer (neuen) Brush-Entity machen. */
  function brushesToEntity(doc: Doc, sel: Sel, cls: string): Doc {
    const ids = sel.b;
    if (!ids.size) return doc;
    const owners = new Set<number>();
    doc.ents.forEach((e, i) => { if (e.brushes.some((b) => ids.has(b.id))) owners.add(i); });
    // Alle Brushes gehören schon zu genau einer Brush-Entity ohne weitere: nur Klasse ändern
    if (owners.size === 1) {
      const i = Array.from(owners)[0], e = doc.ents[i];
      if (i > 0 && e.brushes.every((b) => ids.has(b.id))) {
        const def = EntDefs.get(cls);
        return { ents: doc.ents.map((x, k) => (k === i ? Object.assign({}, x, { kv: Object.assign({}, def.d, x.kv, { classname: cls }) }) : x)) };
      }
    }
    const def = EntDefs.get(cls), target: Entity = { id: Core.newId(), kv: Object.assign({ classname: cls }, def.d), brushes: [] };
    const withTarget = Core.addEntity(doc, target);
    return cleanup(Core.moveBrushesToEntity(withTarget, ids, target.id));
  }
  function brushesToWorld(doc: Doc, sel: Sel): Doc {
    if (!sel.b.size) return doc;
    return cleanup(Core.moveBrushesToEntity(doc, sel.b, doc.ents[0].id));
  }

  // ---- Vertices
  /** Alle Eckpunkte der ausgewählten Brushes, die zu ref passen. mode: '3d' (gleiche Position) | 'proj' (gleiche Ansichtsposition). */
  function vertexGroups(doc: Doc, sel: Sel, ref: Vec3, mode: string, axes: { h: number; v: number }): VertGroup[] {
    const groups: VertGroup[] = [];
    selBrushes(doc, sel).forEach((b) => {
      const idxs: number[] = [];
      b.verts.forEach((v, i) => {
        const same = mode === 'proj'
          ? Math.abs(v[axes.h] - ref[axes.h]) < 0.01 && Math.abs(v[axes.v] - ref[axes.v]) < 0.01
          : Math.abs(v[0] - ref[0]) < 0.01 && Math.abs(v[1] - ref[1]) < 0.01 && Math.abs(v[2] - ref[2]) < 0.01;
        if (same) idxs.push(i);
      });
      if (idxs.length) groups.push({ id: b.id, idxs: idxs });
    });
    return groups;
  }
  /** Gehört der Punkt v zur Punktauswahl vs ({ h, v, pt })? Gleiche Lage in der Projektion der Auswahl-Ansicht. */
  function vertInSel(vs: VertSel | null | undefined, v: Vec3): boolean { return !!vs && Math.abs(v[vs.h] - vs.pt[0]) < 0.01 && Math.abs(v[vs.v] - vs.pt[1]) < 0.01; }
  /** Verschiebt Gruppen um delta. Liefert null, wenn ein Brush dabei ungültig würde. */
  function moveVerts(doc: Doc, groups: VertGroup[], delta: Vec3): Doc | null {
    const byId = new Map(groups.map((g): [string, number[]] => [g.id, g.idxs]));
    let bad = false;
    const r = Core.mapBrushes(doc, new Set(byId.keys()), (b) => {
      const mv = new Map<number, Vec3>();
      (byId.get(b.id) as number[]).forEach((i) => mv.set(i, Core.add(b.verts[i], delta)));
      const nb = Core.moveVertices(b, mv);
      if (!nb) { bad = true; return b; }
      return nb;
    });
    return bad ? null : r;
  }

  // ---- Clip
  function clip(doc: Doc, sel: Sel, n: Vec3, d: number, keep: 'front' | 'back' | 'both', tex?: string): { doc: Doc; sel: Sel } {
    const nsel = emptySel(); nsel.e = new Set(sel.e);
    const r = Core.mapBrushes(doc, sel.b, (b) => {
      const res = Core.clipBrush(b, n, d, keep, tex);
      res.forEach((x) => nsel.b.add(x.id));
      return res;
    });
    return { doc: cleanup(r), sel: nsel };
  }


  // ---- Klick-Auswahl in 2D
  /** Welcher Treffer bei einem Klick gemeint ist. all: Treffer unter dem Zeiger, kleinste Fläche zuerst.
   *  Liegt ein schon gewähltes Element darunter, bleibt es das Ziel (Auswahl und Ziehen gehen nicht an ein kleineres darüber verloren).
   *  Mit alt wandert die Auswahl zum nächsten Treffer. Sonst gilt der oberste (kleinste) Treffer. */
  type Hit = { type: string; id: string };
  function pickHit<T extends Hit>(all: T[], sel: Sel, alt?: boolean): T | null {
    const isSel = (c: Hit) => (c.type === 'b' ? sel.b.has(c.id) : sel.e.has(c.id));
    if (!all.length) return null;
    if (alt && all.length > 1) return all[(all.findIndex(isSel) + 1) % all.length];
    return all.find(isSel) || all[0];
  }

  // ---- Carve
  /** Entities, deren Brushes Carve mitschneidet: die Welt und statische Geometrie (keine Trigger, Türen, Aufzüge) */
  const CARVABLE = new Set(['worldspawn', 'func_detail', 'func_wall']);
  const overlapBox = (a: BBox, b: BBox): boolean => [0, 1, 2].every((i) => a.min[i] < b.max[i] - 0.01 && b.min[i] < a.max[i] - 0.01);
  /** Zieht den konvexen Brush cutter von b ab. null: kein Volumen gemeinsam (b bleibt, wie er ist). Sonst die Reststücke (leer: b liegt ganz im Cutter). */
  function subtractBrush(b: Brush, cutter: Brush): Brush[] | null {
    const out: Brush[] = [];
    let rest: Brush | undefined = b;
    for (const f of cutter.faces) {
      const n = Core.faceNormal(cutter.verts, f.vi), d = Core.dot(n, cutter.verts[f.vi[0]]);
      const front: Brush | undefined = Core.clipBrush(rest, n, d, 'front')[0];
      rest = Core.clipBrush(rest, n, d, 'back')[0];
      if (!rest) return null;
      if (front) out.push(front);
    }
    return out;
  }
  /** Schneidet das Volumen der gewählten Brushes aus allen berührenden Brushes der Welt, von func_detail und func_wall aus (Türen, Fenster). Die gewählten Brushes bleiben stehen.
   *  Neue Schnittflächen übernehmen die Textur der nächsten Fläche des geschnittenen Brushes. Liefert null, wenn nichts berührt wird. */
  function carve(doc: Doc, sel: Sel): { doc: Doc; sel: Sel; cut: number; pieces: number } | null {
    const cutters = selBrushes(doc, sel).filter((c) => c.faces.length >= 4);
    if (!cutters.length || !doc.ents.length) return null;
    const boxes = cutters.map((c) => Core.bbox(c));
    let cut = 0, pieces = 0;
    const ents = doc.ents.map((e, ei) => {
      if (ei > 0 && !CARVABLE.has(e.kv.classname)) return e;
      const brushes: Brush[] = [];
      let touched = false;
      e.brushes.forEach((b) => {
        if (sel.b.has(b.id)) { brushes.push(b); return; }
        let cur: Brush[] = [b], hit = false;
        cutters.forEach((c, i) => {
          const next: Brush[] = [];
          cur.forEach((p) => {
            const r = overlapBox(Core.bbox(p), boxes[i]) ? subtractBrush(p, c) : null;
            if (r) { hit = true; r.forEach((x) => next.push(x)); } else next.push(p);
          });
          cur = next;
        });
        if (!hit) { brushes.push(b); return; }
        touched = true; cut++; pieces += cur.length;
        const used = new Set<string>();
        cur.forEach((x) => {
          if (used.has(x.id)) x.id = Core.newId();
          used.add(x.id);
          brushes.push(x);
        });
      });
      return touched ? Object.assign({}, e, { brushes: brushes }) : e;
    });
    if (!cut) return null;
    return { doc: cleanup({ ents: ents }), sel: cloneSel(sel), cut: cut, pieces: pieces };
  }

  // ---- Merge
  /** Fasst die gewählten Brushes zu einem zusammen, wenn ihre Vereinigung genau die konvexe Hülle ausfüllt (lückenlos, ohne Überlappung).
   *  Die Außenflächen behalten ihre Texturen; verschwindende Berührungsflächen entfallen. Der Merge-Brush nimmt den Platz des ersten ein.
   *  err: 'count' (weniger als zwei), 'entity' (Brushes in verschiedenen Entities), 'convex' (Lücke, Überlappung oder nicht konvex). */
  function merge(doc: Doc, sel: Sel): { doc: Doc; sel: Sel; merged: number } | { err: 'count' | 'entity' | 'convex' } {
    const bs = selBrushes(doc, sel);
    if (bs.length < 2) return { err: 'count' };
    const owner = doc.ents.findIndex((e) => e.brushes.includes(bs[0]));
    if (!bs.every((b) => doc.ents[owner].brushes.includes(b))) return { err: 'entity' };
    const hull = Core.buildHull(bs.reduce((a: Vec3[], b) => a.concat(b.verts), []));
    if (!hull) return { err: 'convex' };
    const faces = hull.loops.map((vi) => {
      const n = Core.faceNormal(hull.verts, vi), d = Core.dot(n, hull.verts[vi[0]]);
      let best: Face | null = null, bestA = -1;
      bs.forEach((b) => b.faces.forEach((f) => {
        const fn = Core.faceNormal(b.verts, f.vi);
        if (Core.dot(fn, n) < 0.999 || f.vi.some((i) => Math.abs(Core.dot(n, b.verts[i]) - d) > 0.05)) return;
        const a = polyArea3(f.vi.map((i) => b.verts[i]));
        if (a > bestA) { bestA = a; best = f; }
      }));
      const src = best as Face | null;
      return src ? Object.assign({}, src, { vi: vi }) : Object.assign({ vi: vi }, Core.defaultFace(bs[0].faces[0].tex, n));
    });
    const nb: Brush = { id: bs[0].id, verts: hull.verts, faces: faces };
    const want = bs.reduce((s, b) => s + volume(b), 0), got = volume(nb);
    if (Math.abs(got - want) > Math.max(1, 1e-4 * got)) return { err: 'convex' };
    const ids = new Set(bs.map((b) => b.id));
    let placed = false;
    const ents = doc.ents.map((e, i) => {
      if (i !== owner) return e;
      const out: Brush[] = [];
      e.brushes.forEach((b) => { if (!ids.has(b.id)) out.push(b); else if (!placed) { placed = true; out.push(nb); } });
      return Object.assign({}, e, { brushes: out });
    });
    const nsel = cloneSel(sel); nsel.b = new Set([nb.id]);
    return { doc: { ents: ents }, sel: nsel, merged: bs.length };
  }
  function polyArea3(P: Vec3[]): number {
    let ax = 0, ay = 0, az = 0;
    for (let i = 1; i < P.length - 1; i++) {
      const c = Core.cross(Core.sub(P[i], P[0]), Core.sub(P[i + 1], P[0]));
      ax += c[0]; ay += c[1]; az += c[2];
    }
    return Math.sqrt(ax * ax + ay * ay + az * az) / 2;
  }

  // ---- Aushöhlen
  /** Höhlt jeden gewählten Brush aus: Wandstärke t, Innenfläche bekommt innerTex. Liefert null, wenn die Wand zu dick ist. */
  function hollow(doc: Doc, sel: Sel, t: number, innerTex?: string): { doc: Doc; sel: Sel } | null {
    if (!(t > 0)) return null;
    const nsel = emptySel(); nsel.e = new Set(sel.e);
    let bad = false;
    const r = Core.mapBrushes(doc, sel.b, (b) => {
      const pieces: Brush[] = [];
      let rest = b;
      for (let k = 0; k < b.faces.length && !bad; k++) {
        const n = Core.faceNormal(b.verts, b.faces[k].vi), d = Core.dot(n, b.verts[b.faces[k].vi[0]]), dd = d - t;
        const wall = Core.clipBrush(rest, n, dd, 'front', innerTex)[0];
        const back = Core.clipBrush(rest, n, dd, 'back', innerTex)[0];
        if (!back) { bad = true; break; }
        if (wall) {
          wall.id = Core.newId();
          wall.srcText = undefined;
          wall.faces = wall.faces.map((f) => {
            const fn = Core.faceNormal(wall.verts, f.vi);
            const onCut = Core.dot(fn, n) < -0.999 && f.vi.every((i) => Math.abs(Core.dot(n, wall.verts[i]) - dd) < 0.05);
            return onCut ? Object.assign({}, f, Core.defaultFace(innerTex || f.tex, fn), { vi: f.vi }) : f;
          });
          pieces.push(wall);
        }
        rest = back;
      }
      if (bad || !pieces.length) { bad = true; return b; }
      pieces.forEach((x) => nsel.b.add(x.id));
      return pieces;
    });
    return bad ? null : { doc: r, sel: nsel };
  }

  /** Eine Box-Form in der Welt erzeugen. */
  function addShape(doc: Doc, shapeFn: () => Brush | null): { doc: Doc; sel: Sel } | null {
    const b = shapeFn();
    if (!b) return null;
    return addBrushList(doc, [b]);
  }

  return {
    emptySel, cloneSel, isEmpty, isPointEnt, selBrushes, selEnts, selBBox, move, resize, setBBox, remove, rotate, mirror,
    toClip, paste, duplicate, addBrushList, createEntity, applyTexture, setFace, setFaces, setKV, delKV, renameKV, brushesToEntity, brushesToWorld,
    vertexGroups, vertInSel, moveVerts, clip, pickHit, carve, merge, hollow, addShape, cleanup, fmtOrigin
  };
})(Core, EntDefs);
export default Cmd;
